import { isTestEnv } from "@excalidraw/common";

export class RoomCodeServiceError extends Error {
  status?: number;
  reason: "CONFIG" | "HTTP" | "NETWORK";

  constructor(
    message: string,
    reason: RoomCodeServiceError["reason"],
    status?: number,
  ) {
    super(message);
    this.name = "RoomCodeServiceError";
    this.status = status;
    this.reason = reason;
  }
}

const normalizeBaseUrl = (value: string | undefined) => {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    url.pathname = url.pathname.replace(/\/+$/, "");
    return url.toString();
  } catch {
    return value.replace(/\/+$/, "");
  }
};

const deriveBaseFromWsUrl = () => {
  const wsUrl = import.meta.env.VITE_APP_WS_SERVER_URL;
  if (!wsUrl) {
    return null;
  }
  try {
    const url = new URL(wsUrl);
    url.protocol = url.protocol === "wss:" ? "https:" : "http:";
    url.pathname = `${url.pathname.replace(/\/+$/, "")}/api`;
    url.hash = "";
    url.search = "";
    return url.toString();
  } catch {
    return null;
  }
};

const maybeUpgradeToHttps = (value: string | null) => {
  if (!value) {
    return value;
  }
  if (typeof window === "undefined" || window.location.protocol !== "https:") {
    return value;
  }
  try {
    const url = new URL(value);
    if (
      url.protocol === "http:" &&
      url.hostname !== "localhost" &&
      url.hostname !== "127.0.0.1"
    ) {
      url.protocol = "https:";
      return url.toString();
    }
  } catch {
    if (/^http:\/\//i.test(value)) {
      return value.replace(/^http:/i, "https:");
    }
  }
  return value;
};

const COLLAB_API_BASE = maybeUpgradeToHttps(
  normalizeBaseUrl(import.meta.env.VITE_APP_COLLAB_API_BASE_URL) ||
    deriveBaseFromWsUrl(),
);

const getBaseUrl = () => COLLAB_API_BASE;

const buildUrl = (path: string) => {
  const base = getBaseUrl();
  if (!base) {
    throw new RoomCodeServiceError(
      "Room code API base URL is not configured",
      "CONFIG",
    );
  }
  return `${base}${path}`;
};

const parseResponse = async (response: Response) => {
  let data: any = null;
  try {
    data = await response.json();
  } catch {
    // ignore parse errors
  }

  if (!response.ok) {
    const message =
      typeof data?.error === "string" ? data.error : "Room code request failed";
    throw new RoomCodeServiceError(message, "HTTP", response.status);
  }
  return data;
};

const request = async (path: string, init?: RequestInit) => {
  const url = buildUrl(path);
  const config: RequestInit = {
    credentials: "include",
    ...init,
  };
  try {
    const response = await fetch(url, config);
    return await parseResponse(response);
  } catch (error: any) {
    if (error instanceof RoomCodeServiceError) {
      throw error;
    }
    throw new RoomCodeServiceError(
      error?.message ?? "Room code network request failed",
      "NETWORK",
    );
  }
};

export const createRoomCode = async ({
  roomId,
  roomKey,
}: {
  roomId: string;
  roomKey: string;
}) => {
  const body = JSON.stringify({ roomId, roomKey });

  const data = await request("/room-code", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
  });

  return typeof data?.code === "string" ? data.code : null;
};

export const resolveRoomCode = async (code: string) => {
  if (!/^\d{1,6}$/.test(code)) {
    throw new RoomCodeServiceError("Invalid code format", "HTTP", 400);
  }
  const data = await request(`/room-code/${code}`, {
    method: "GET",
  });
  if (typeof data?.roomId !== "string" || typeof data?.roomKey !== "string") {
    throw new RoomCodeServiceError("Incomplete room data", "HTTP", 502);
  }
  return {
    roomId: data.roomId,
    roomKey: data.roomKey,
  };
};

export const canUseRoomCodes = () => {
  if (isTestEnv()) {
    return true;
  }
  return Boolean(getBaseUrl());
};
